import { createSlice } from "@reduxjs/toolkit";
import {
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../../reducer/notifications/notificationsReducer";

const initialState = {
  notifications: [],
  pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
  unreadCount: 0,
  loading: false,
  lastFetched: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Add real-time notification from socket
    addRealtimeNotification: (state, action) => {
      const newNotification = action.payload;
      const existingIndex = state.notifications.findIndex(
        n => n.notification_id === newNotification.notification_id ||
             n.id === newNotification.id
      );

      if (existingIndex === -1) {
        state.notifications.unshift({
          id: newNotification.id || Date.now(),
          notification_id: newNotification.notification_id,
          user_id: newNotification.user_id,
          notification_type: newNotification.notification_type,
          title: newNotification.title || null,
          title_arabic: newNotification.title_arabic || null,
          body: newNotification.body || '',
          body_arabic: newNotification.body_arabic || null,
          data: newNotification.data || null,
          status: 'sent',
          created_at: new Date(newNotification.timestamp || Date.now()).toISOString(),
        });
        state.unreadCount += 1;
      }
    },

    markAsReadLocally: (state, action) => {
      const id = action.payload;
      const notification = state.notifications.find(
        n => n.id === id || n.notification_id === id || n.recipient === id,
      );
      if (notification) {
        const wasUnread = !(
          notification.isRead ||
          notification.readAt ||
          notification.read_at ||
          notification.status === 'read'
        );
        // Drive read state off isRead/readAt (matches the backend + list response)
        notification.isRead = true;
        notification.readAt = new Date().toISOString();
        notification.status = 'read';
        if (wasUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },

    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    // Get notifications
    builder
      .addCase(getUserNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data?.notifications && Array.isArray(data.notifications)) {
          state.notifications = data.notifications;
          state.pagination = data.pagination || state.pagination;
        } else if (Array.isArray(data)) {
          state.notifications = data;
        }
        // Recalculate unread count from read state (isRead/readAt), not the
        // delivery status — the server never flips `status` on read.
        state.unreadCount = state.notifications.filter(
          n => !(n.isRead || n.readAt || n.read_at || n.status === 'read'),
        ).length;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(getUserNotifications.rejected, (state) => {
        state.loading = false;
      });

    // Get unread count
    builder
      .addCase(getUnreadNotificationCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload || 0;
      });

    // Mark as read
    builder
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const id = action.payload;
        const notification = state.notifications.find(n => n.id === id || n.notification_id === id || n.recipient === id);
        if (notification) {
          // Only decrement if still unread — markAsReadLocally already applied
          // the optimistic update on click, so this must not double-decrement.
          const wasUnread = !(
            notification.isRead ||
            notification.readAt ||
            notification.read_at ||
            notification.status === 'read'
          );
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
          notification.status = 'read';
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });

    // Mark all as read
    builder
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(n => {
          n.isRead = true;
          n.readAt = new Date().toISOString();
          n.status = 'read';
        });
        state.unreadCount = 0;
      });

    // Delete
    builder
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = action.payload;
        const index = state.notifications.findIndex(n => n.id === id || n.notification_id === id || n.recipient === id);
        if (index !== -1) {
          const notification = state.notifications[index];
          const wasUnread = !(
            notification.isRead ||
            notification.readAt ||
            notification.read_at ||
            notification.status === 'read'
          );
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.notifications.splice(index, 1);
        }
      });
  },
});

export const {
  addRealtimeNotification,
  markAsReadLocally,
  clearNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
